import React, { useMemo, useCallback } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { PrivateImageGeneratorClient } from './api/client';
import { useDeleteWorkbookImage } from '@/hooks/content-generator/use-workbook-images';
import { useToast } from '@/hooks/errors/use-toast';
import { useDebouncedValue } from '@/lib/utils';
import type { PrivateImageGeneratorItem, PrivateImageGeneratorFilter } from './types';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook for private image library data with infinite scroll and proper deletion
 */
export const usePrivateImageLibraryData = (filters: PrivateImageGeneratorFilter) => {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Debounce description search to avoid excessive API calls
  const debouncedDescription = filters.description || '';

  // Query for private images with infinite scroll
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: ['private-image-generator', user?.id, debouncedDescription, filters.projectId, filters],
    enabled: !!user?.id,
    queryFn: async ({ pageParam = 0 }) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const limit = 12;
      const offset = pageParam * limit;
      
      const images = await PrivateImageGeneratorClient.fetchPrivateImages(
        user.id,
        debouncedDescription, // Use debounced description for search
        limit,
        offset,
        filters.projectId, // Pass project filter
        {
          styleId: filters.styleId,
          influenceId: filters.influenceId,
          backgroundId: filters.backgroundId,
          environmentIds: filters.environmentIds,
          moodIds: filters.moodIds,
          colorIds: filters.colorIds,
          imageType: filters.imageType,
          width: filters.width,
          height: filters.height,
        }
      );
      
      // Transform PrivateImageGeneratorResponse to PrivateImageGeneratorItem
      const transformedImages: PrivateImageGeneratorItem[] = images.map(item => ({
        ...item,
        workbook_id: item.workbook_id, // Keep actual workbook_id (can be null or project ID)
        page_id: null, // Always null for private images
        deleted_at: null // Private images are not soft-deleted
      }));
      
      return {
        images: transformedImages,
        nextPage: transformedImages.length === limit ? pageParam + 1 : undefined,
        hasMore: transformedImages.length === limit
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0
  });

  // Flatten paginated results
  const images = useMemo(() => {
    return data?.pages.flatMap(page => page.images) || [];
  }, [data]);

  // Query for total count (for display purposes)
  const { data: totalCount } = useQuery({
    queryKey: ['private-image-generator-count', user?.id, debouncedDescription, filters.projectId],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return 0;
      return await PrivateImageGeneratorClient.getPrivateImageCount(
        user.id, 
        debouncedDescription, 
        filters.projectId,
        {
          styleId: filters.styleId,
          influenceId: filters.influenceId,
          backgroundId: filters.backgroundId,
          environmentIds: filters.environmentIds,
          moodIds: filters.moodIds,
          colorIds: filters.colorIds,
          imageType: filters.imageType,
          width: filters.width,
          height: filters.height,
        }
      );
    }
  });

  // Custom RLS-aware delete mutation for private images
  const deleteImageMutation = useMutation({
    mutationFn: async (imageId: string) => {
      console.log('[usePrivateImageLibrary] Custom deleteImageMutation started:', {
        imageId: imageId,
        userId: user?.id,
        timestamp: new Date().toISOString()
      });
      
      if (!user?.id) {
        const error = new Error('User not authenticated');
        console.error('[usePrivateImageLibrary] Authentication check failed:', error);
        throw error;
      }
      
      // Use authenticated Supabase client with proper RLS context
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found');
      }
      
      console.log('[usePrivateImageLibrary] Getting image data for deletion...');
      // First get the image data to get storage path - this should work with RLS
      const { data: imageData, error: fetchError } = await supabase
        .from('workbook_images')
        .select('*')
        .eq('id', imageId)
        .single();

      if (fetchError) {
        console.error('[usePrivateImageLibrary] Failed to fetch image data:', fetchError);
        throw new Error(`Failed to fetch image data: ${fetchError.message}`);
      }

      console.log('[usePrivateImageLibrary] Deleting from storage:', imageData.storage_path);
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('workbook_images')
        .remove([imageData.storage_path]);

      if (storageError) {
        console.error('[usePrivateImageLibrary] Storage deletion error (continuing anyway):', storageError);
        // Continue with database deletion even if storage fails
      }

      console.log('[usePrivateImageLibrary] Deleting from database...');
      // Delete from database using RLS-aware context
      const { error: dbError } = await supabase
        .from('workbook_images')
        .delete()
        .eq('id', imageId);

      if (dbError) {
        console.error('[usePrivateImageLibrary] Database deletion failed:', dbError);
        throw new Error(`Failed to delete image record: ${dbError.message}`);
      }
      
      console.log('[usePrivateImageLibrary] Universal storage cleanup automatic via CASCADE foreign key');
      
      console.log('[usePrivateImageLibrary] Image deleted successfully:', imageId);
      return imageId;
    },
    onSuccess: (deletedImageId) => {
      // Invalidate private image generator queries
      queryClient.invalidateQueries({ queryKey: ['private-image-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-generator-count'] });
      
      // Invalidate Project Image Library queries (since private images can appear in projects)
      queryClient.invalidateQueries({ queryKey: ['private-image-library'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count'] });
      
      // Invalidate workbook image queries (comprehensive coverage)
      queryClient.invalidateQueries({ queryKey: ['workbook-images'] });
      
      // Invalidate universal storage queries (for private_image content type)
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-library-data'] });
      
      toast({
        title: "Image deleted",
        description: "The image has been permanently removed from your private library.",
      });
    },
    onError: (error: Error) => {
      console.error('Delete image error:', error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete image. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Function to handle deleting images
  const handleDeleteImage = useCallback(async (id: string) => {
    console.log('[usePrivateImageLibrary] handleDeleteImage called with ID:', {
      imageId: id,
      userId: user?.id,
      timestamp: new Date().toISOString()
    });
    
    if (!user?.id) {
      const error = new Error('User not authenticated');
      console.error('[usePrivateImageLibrary] handleDeleteImage - user not authenticated:', error);
      return;
    }
    
    try {
      console.log('[usePrivateImageLibrary] Calling deleteImageMutation.mutateAsync with ID:', id);
      // Use hard delete for private images - completely removes from storage and database
      await deleteImageMutation.mutateAsync(id);
      console.log('[usePrivateImageLibrary] deleteImageMutation completed successfully for ID:', id);
    } catch (error) {
      // Error handling is done in the mutation
      console.error('[usePrivateImageLibrary] handleDeleteImage failed for ID:', {
        imageId: id,
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  }, [user?.id, deleteImageMutation]);

  return {
    images,
    isLoading,
    isError,
    loadMore: fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    handleDeleteImage,
    isDeleting: deleteImageMutation.isPending,
    searchTerm: debouncedDescription, // Return the debounced search term
    setSearchTerm: () => {} // No-op since we handle debouncing internally
  };
}; 